import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";

import { Reveal, MaskedLine } from "@/components/Motion";
import CTABanner from "@/components/CTABanner";
import BookButton from "@/components/BookButton";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const PageHeader = () => {
  const { t } = useLang();
  return (
    <section data-testid="packages-header" className="bg-alabaster pb-20 pt-40">
      <SEO
        title="Ongoing Support Packages from $300/month | VCK Business Lab"
        description="Monthly virtual support plans — Essential, Professional, Executive, and Global Priority. Loyalty rewards, referral program, and additional hours at add-on rates."
        path="/packages"
      />
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold-muted">{t("packages.overline")}</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("packages.line1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold-muted">{t("packages.line2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {t("packages.intro")}
          </span>
        </MaskedLine>
      </div>
    </section>
  );
};

const PackageCard = ({ pkg, index }) => {
  const { t } = useLang();

  return (
    <Reveal delay={index * 0.08} className="h-full">
      <div
        data-testid={`package-card-${pkg.slug}`}
        className={`card-lift relative flex h-full flex-col rounded-xl bg-white p-8 ${
          pkg.popular ? "border-2 border-gold shadow-xl" : "border border-obsidian/10 hover:border-gold/50 hover:shadow-lg"
        }`}
      >
        {pkg.popular && (
          <span
            data-testid="package-popular-badge"
            className="absolute -top-3.5 start-8 rounded-full bg-gold px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-obsidian"
          >
            {t("packages.mostRequested")}
          </span>
        )}
        <p className="overline text-gold-muted">
          {pkg.slug === "global-priority" ? t("packages.tailored") : t("packages.startingFrom")}
        </p>
        <h2 className="mt-4 font-serif text-3xl text-obsidian">{pkg.name}</h2>
        <p data-testid={`package-price-${pkg.slug}`} className="mt-3 font-mono text-sm tracking-wide text-gold-muted">
          {pkg.price}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">{pkg.fit}</p>
        <ul className="mt-8 flex-1 space-y-3.5 border-t border-obsidian/10 pt-8">
          {pkg.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
              {f}
            </li>
          ))}
        </ul>
        <Link
          to={`/contact?plan=${pkg.slug}`}
          data-testid={`package-quote-button-${pkg.slug}`}
          className={`${pkg.popular ? "btn-gold" : "btn-outline-dark"} mt-8 w-full text-center`}
        >
          {t("packages.requestQuote")}
        </Link>
        <p className="mt-3 text-center text-xs leading-relaxed text-slate-400">{t("packages.quoteNote")}</p>
      </div>
    </Reveal>
  );
};

const PricingDisclaimer = () => {
  const { t } = useLang();
  return (
    <section data-testid="packages-pricing-disclaimer" className="bg-alabaster py-20 lg:py-24">
      <div className="mx-auto max-w-3xl px-6 text-center sm:px-12">
        <Reveal>
          <p className="overline text-gold-muted">{t("packages.disOverline")}</p>
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-obsidian sm:text-2xl">
            {t("packages.disQuote")}
          </p>
          <p className="mt-6 text-sm text-slate-500">{t("packages.disNote")}</p>
        </Reveal>
      </div>
    </section>
  );
};

const LoyaltyRewards = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="loyalty-rewards-section" className="relative overflow-hidden bg-obsidian py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-gold/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Reveal className="max-w-3xl">
          <p className="overline text-gold">{t("packages.loyaltyOverline")}</p>
          <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-white sm:text-4xl">
            {t("packages.loyaltyTitle")}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">{t("packages.loyaltyIntro")}</p>
        </Reveal>

        <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {c.loyaltyTiers.map((tier, i) => (
            <Reveal key={i} delay={i * 0.08} className="h-full">
              <div
                data-testid={`loyalty-tier-${i + 1}`}
                className={`card-lift relative flex h-full flex-col rounded-xl p-8 ${
                  tier.premium
                    ? "border border-gold/60 bg-gradient-to-b from-gold/15 to-obsidian-card shadow-2xl"
                    : "border border-white/10 bg-obsidian-card hover:border-gold/40"
                }`}
              >
                {tier.premium && (
                  <span className="absolute -top-3.5 start-8 rounded-full bg-gold px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-obsidian">
                    {t("packages.bestValue")}
                  </span>
                )}
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">{tier.months}</p>
                <p className={`mt-4 font-serif text-3xl leading-tight ${tier.premium ? "text-gold" : "text-white"}`}>
                  {tier.hours}
                </p>
                {tier.note && <p className="mt-4 text-sm leading-relaxed text-slate-400">{tier.note}</p>}
                {tier.extras.length > 0 && (
                  <ul className="mt-6 flex-1 space-y-3 border-t border-white/10 pt-6">
                    {tier.extras.map((extra) => (
                      <li key={extra} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        {extra}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="welcome-package-block"
            className="mt-20 grid gap-10 rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 via-obsidian-card to-obsidian-card p-10 sm:p-14 lg:grid-cols-2"
          >
            <div>
              <p className="overline text-gold">{t("packages.welcomeOverline")}</p>
              <h3 className="mt-4 font-serif text-3xl leading-tight text-white">{t("packages.welcomeTitle")}</h3>
              <p className="mt-5 text-sm leading-relaxed text-slate-400">{t("packages.welcomeText")}</p>
              <p className="mt-4 font-serif text-lg italic text-gold">{t("packages.welcomeNote")}</p>
            </div>
            <div className="flex flex-col justify-center">
              <p className="overline text-slate-400">{t("packages.welcomeInclude")}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {t("packages.welcomeItems").map((item, i) => (
                  <span
                    key={i}
                    data-testid={`welcome-package-item-${i + 1}`}
                    className="rounded-full border border-gold/30 bg-white/5 px-5 py-2.5 text-sm text-slate-200"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-20 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline text-gold">{t("packages.bonusOverline")}</p>
            <h3 className="mt-4 font-serif text-2xl leading-tight text-white sm:text-3xl">
              {t("packages.bonusTitle")}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("packages.bonusText")}</p>
            <ul className="mt-6 space-y-3">
              {c.loyaltyServices.map((service, i) => (
                <li key={i} data-testid={`bonus-hour-service-${i + 1}`} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {service}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              data-testid="loyalty-cta-block"
              className="flex h-full flex-col justify-center rounded-2xl border border-white/10 bg-obsidian-card p-10"
            >
              <p className="overline text-gold">{t("packages.builtOverline")}</p>
              <h3 className="mt-4 font-serif text-2xl leading-tight text-white sm:text-3xl">{t("packages.builtTitle")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("packages.builtText")}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <BookButton testid="loyalty-book-consultation-button">{t("packages.builtBook")}</BookButton>
                <span className="font-serif italic text-slate-500">{t("packages.builtOr")}</span>
                <Link to="/contact" data-testid="loyalty-request-quote-button" className="btn-outline-light">
                  {t("packages.builtQuote")}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-20 text-center">
          <p data-testid="loyalty-welcome-line" className="font-serif text-2xl italic leading-relaxed text-slate-300 sm:text-3xl">
            {t("packages.welcomeLine")}
          </p>
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-gold-muted">
            {t("packages.welcomeTagline")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const ReferralProgram = () => {
  const { t } = useLang();
  return (
    <section data-testid="referral-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Reveal className="max-w-3xl">
          <p className="overline text-gold-muted">{t("packages.referOverline")}</p>
          <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-obsidian sm:text-4xl">
            {t("packages.referTitle")}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">{t("packages.referIntro")}</p>
        </Reveal>

        <div className="mt-16 grid gap-10 sm:grid-cols-3">
          {t("packages.referSteps").map((step, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`referral-step-${i + 1}`} className="border-t border-obsidian/10 pt-8">
                <p className="font-mono text-4xl font-light text-gold-muted/70">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-2xl text-obsidian">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <p className="overline text-gold-muted">{t("packages.referRewardsTitle")}</p>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {t("packages.referTiers").map((tier, i) => (
            <Reveal key={i} delay={i * 0.08} className="h-full">
              <div
                data-testid={`referral-tier-${i + 1}`}
                className={`card-lift flex h-full flex-col rounded-xl p-7 ${
                  i === 3
                    ? "border border-gold/60 bg-white shadow-xl"
                    : "border border-obsidian/10 bg-white hover:border-gold/50 hover:shadow-lg"
                }`}
              >
                <p className="text-sm font-medium text-slate-500">{tier.plan}</p>
                <p className={`mt-4 font-serif text-2xl leading-snug ${i === 3 ? "text-gold-muted" : "text-obsidian"}`}>
                  {tier.hours}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline text-gold-muted">{t("packages.referMoreTitle")}</p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">{t("packages.referMoreText")}</p>
            <div className="mt-8 space-y-6">
              {t("packages.referMilestones").map((m, i) => (
                <div key={i} data-testid={`referral-milestone-${i + 1}`} className="flex items-start gap-5">
                  <span className="font-serif text-3xl font-light leading-tight text-gold-muted">{m.count}</span>
                  <p className="mt-1.5 text-sm text-slate-600">{m.reward}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-obsidian/10 bg-white p-10">
              <p className="overline text-gold-muted">{t("packages.referThanksOverline")}</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{t("packages.referThanksText")}</p>
              <ul className="mt-6 space-y-3">
                {t("packages.referServices").map((s, i) => (
                  <li key={i} data-testid={`referral-service-${i + 1}`} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-20">
          <div data-testid="referral-cta-block" className="relative overflow-hidden rounded-2xl bg-obsidian p-10 text-center sm:p-14">
            <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[480px] -translate-x-1/2 rounded-full bg-gold/10 blur-[100px]" />
            <div className="relative">
              <h3 className="font-serif text-3xl leading-tight text-white sm:text-4xl">{t("packages.referCtaTitle")}</h3>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">{t("packages.referCtaText")}</p>
              <div className="mt-8 flex justify-center">
                <Link to="/contact?intent=referral" data-testid="referral-refer-business-button" className="btn-gold">
                  {t("packages.referCtaBtn")}
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Packages() {
  const { t, c } = useLang();
  return (
    <div data-testid="packages-page">
      <PageHeader />
      <section className="bg-alabaster pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
            {c.plans.map((p, i) => (
              <PackageCard key={p.slug} pkg={p} index={i} />
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <p
              data-testid="packages-pricing-note"
              className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-slate-500"
            >
              {t("packages.note")}
            </p>
            <p
              data-testid="packages-availability-note"
              className="mx-auto mt-4 max-w-3xl text-center text-xs leading-relaxed text-slate-400"
            >
              {t("packages.availabilityNote")}
            </p>
          </Reveal>
        </div>
      </section>
      <LoyaltyRewards />
      <ReferralProgram />
      <ComparisonTable />
      <PricingDisclaimer />
      <CTABanner />
    </div>
  );
}

const ComparisonTable = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="comparison-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Reveal className="max-w-3xl">
          <p className="overline text-gold-muted">{t("packages.compareOverline")}</p>
          <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-obsidian sm:text-4xl">
            {t("packages.compareTitle")}
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mt-14 overflow-x-auto">
          <table data-testid="comparison-table" className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-gold/40">
                <th className="py-4 pe-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
                  {t("packages.compareIncluded")}
                </th>
                {c.comparison.columns.map((col, i) => (
                  <th
                    key={col}
                    data-testid={`comparison-col-${i}`}
                    className={`px-4 py-4 font-serif text-lg ${i === 1 ? "text-gold-muted" : "text-obsidian"}`}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.comparison.rows.map((row, ri) => (
                <tr key={ri} data-testid={`comparison-row-${ri}`} className="border-b border-obsidian/10">
                  <td className="py-4 pe-6 text-sm font-medium text-obsidian">{row.label}</td>
                  {row.values.map((v, vi) => (
                    <td key={vi} className="px-4 py-4">
                      {v === true ? (
                        <Check className="h-4 w-4 text-gold-muted" />
                      ) : v === false ? (
                        <Minus className="h-4 w-4 text-slate-300" />
                      ) : (
                        <span className="text-sm text-slate-600">{v}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
        <Reveal delay={0.2} className="mt-10">
          <Link to="/contact" data-testid="comparison-request-quote-button" className="btn-gold">
            {t("packages.requestQuote")}
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
